/**
 * MEL-MR-2 — per-section meta-instruction-leak chip + Regenerate
 * button. Followup to MEL-1 (row 0.99261) and MEL-MR-1 (regen retry).
 *
 * Renders an amber chip immediately above a section when:
 *   1. `report.qualityWarnings` contains a `meta-instruction-leak`
 *      entry, AND
 *   2. that entry's `samples[]` contains at least one match for
 *      `sectionId`.
 *
 * The chip composes side-by-side with the RNH-2 stance-mismatch chip
 * — they read from the same `qualityWarnings` array but render
 * independently. Each chip explains its own concern.
 *
 * Admin-only: gated on `isAdmin` because the Regenerate button calls
 * a Gemini-backed store action whose cost should not be exposed to
 * end users until product validates the prompt strengthening. End
 * users still see the (cheaper) RNH-2 chip and the existing
 * SectionQuarantineBanner; only the MEL-specific affordance is gated.
 *
 * Pure presentational component — all side effects flow through the
 * `onRegenerate` callback that the parent wires to `triggerSectionRegen`
 * on the store.
 *
 * REPORT PAGE (2026-10-13, William: no banners on report pages): renders
 * nothing where `useInSectionNoticesVisible()` is false — the interactive
 * report page, where this block read as an in-section banner with its own
 * Regenerate button. Admins reach the same section re-run from the header's
 * ⋮ "Section quality notes" dialog. Other hosts are unchanged.
 */
import React from 'react';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import type { SiteRagReport } from '../../../types/siteRag';
import { useInSectionNoticesVisible } from './inSectionNotices';

export interface MetaLeakChipProps {
  /** The whole report so the chip can read qualityWarnings. */
  report: SiteRagReport;
  /** The section this chip is rendered next to. Must match the
   *  `sectionId` value stamped by `narrativeHygiene.stripMetaInstructions`
   *  (e.g. `'executiveSummary'`, `'summary'`, `'sitingNotes'`, …). */
  sectionId: string;
  /** Human-readable section title for the chip body. */
  sectionLabel: string;
  /** Gates the [Regenerate] button + the chip itself. End users see
   *  nothing until product validates the gen-time prompt strengthening. */
  isAdmin: boolean;
  /** Called when the admin clicks [Regenerate]. The handler is
   *  responsible for the actual store / Gemini call; the chip only
   *  toggles the in-flight spinner. */
  onRegenerate?: (sectionId: string) => Promise<void> | void;
}

/**
 * Pure predicate: does the report's `meta-instruction-leak` warning
 * include a sample for this sectionId? Exported for tests.
 */
export function hasMetaLeakForSection(report: SiteRagReport, sectionId: string): boolean {
  const warnings = Array.isArray(report?.qualityWarnings) ? report.qualityWarnings : [];
  for (const w of warnings) {
    if (w.type !== 'meta-instruction-leak') continue;
    const samples = (w as { samples?: ReadonlyArray<{ sectionId: string }> }).samples;
    if (!Array.isArray(samples)) continue;
    for (const s of samples) {
      if (s.sectionId === sectionId) return true;
    }
  }
  return false;
}

/**
 * Read the family tags that fired for this section. Useful for the
 * chip body so the admin sees which directive shape was detected.
 * Returns deduplicated families in first-seen order.
 */
export function familiesForSection(report: SiteRagReport, sectionId: string): string[] {
  const warnings = Array.isArray(report?.qualityWarnings) ? report.qualityWarnings : [];
  const families: string[] = [];
  const seen = new Set<string>();
  for (const w of warnings) {
    if (w.type !== 'meta-instruction-leak') continue;
    const samples = (w as { samples?: ReadonlyArray<{ sectionId: string; family: string }> }).samples;
    if (!Array.isArray(samples)) continue;
    for (const s of samples) {
      if (s.sectionId !== sectionId) continue;
      if (seen.has(s.family)) continue;
      seen.add(s.family);
      families.push(s.family);
    }
  }
  return families;
}

export const MetaLeakChip: React.FC<MetaLeakChipProps> = ({
  report,
  sectionId,
  sectionLabel,
  isAdmin,
  onRegenerate,
}) => {
  const [regenerating, setRegenerating] = React.useState(false);
  const visibleHere = useInSectionNoticesVisible();

  // Reasons the chip renders nothing — guard early so the DOM stays clean
  // on the 99% case where no leak fired (and always on the report page).
  if (!visibleHere) return null;
  if (!isAdmin) return null;
  if (!hasMetaLeakForSection(report, sectionId)) return null;

  const families = familiesForSection(report, sectionId);
  const familyLabel = families.length > 0 ? families.join(', ') : 'meta-instruction-leak';

  const onClick = async () => {
    if (regenerating) return;
    if (!onRegenerate) return;
    setRegenerating(true);
    try {
      await onRegenerate(sectionId);
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div
      data-testid="meta-leak-chip"
      data-section-id={sectionId}
      data-families={families.join(',')}
      className="rounded-md border border-warning/40 bg-warning-subtle px-3 py-2 mb-2 flex items-start gap-2"
      role="status"
      aria-live="polite"
    >
      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-warning" aria-hidden />
      <div className="flex-1 min-w-0 space-y-1">
        <p className="text-sm font-medium text-warning leading-snug">
          <span className="font-semibold">{sectionLabel}</span>
          {' '}– meta-instruction leak detected
        </p>
        <p className="type-body text-warning/90 leading-relaxed">
          Gemini's QA-review directive leaked into this section's prose ({familyLabel}).
          The strip removed the offending sentence; the section may need a regenerate
          to repopulate cleanly.
        </p>
      </div>
      {onRegenerate ? (
        <button
          type="button"
          onClick={onClick}
          disabled={regenerating}
          aria-label={`Regenerate ${sectionLabel}`}
          data-testid="meta-leak-chip-regenerate"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-warning/50 bg-surface-1 px-2.5 py-1 type-body font-bold uppercase tracking-widest text-warning hover:bg-warning-subtle hover:text-warning disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {regenerating ? (
            <Loader2 className="w-3 h-3 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="w-3 h-3" aria-hidden />
          )}
          {regenerating ? 'Regenerating…' : 'Regenerate'}
        </button>
      ) : null}
    </div>
  );
};

export default MetaLeakChip;
